import { useEffect, useState } from "react";

const MAX_ENTITY_PREVIEW_BLOB_CACHE_ENTRIES = 3000;

const entityPreviewBlobUrlCache = new Map<string, string>();
const pendingEntityPreviewBlobUrlLookups = new Map<string, Promise<string | null>>();

const touchEntityPreviewBlobCacheEntry = (contentHash: string): string | undefined => {
  const existingBlobUrl = entityPreviewBlobUrlCache.get(contentHash);
  if (!existingBlobUrl) {
    return undefined;
  }

  entityPreviewBlobUrlCache.delete(contentHash);
  entityPreviewBlobUrlCache.set(contentHash, existingBlobUrl);

  return existingBlobUrl;
};

const setEntityPreviewBlobCacheEntry = (contentHash: string, blobUrl: string): void => {
  const previousBlobUrl = entityPreviewBlobUrlCache.get(contentHash);
  if (previousBlobUrl && previousBlobUrl !== blobUrl) {
    URL.revokeObjectURL(previousBlobUrl);
  }

  entityPreviewBlobUrlCache.delete(contentHash);
  entityPreviewBlobUrlCache.set(contentHash, blobUrl);

  while (entityPreviewBlobUrlCache.size > MAX_ENTITY_PREVIEW_BLOB_CACHE_ENTRIES) {
    const oldestEntry = entityPreviewBlobUrlCache.entries().next().value;
    if (!oldestEntry) {
      break;
    }

    const [oldestContentHash, oldestBlobUrl] = oldestEntry;
    entityPreviewBlobUrlCache.delete(oldestContentHash);
    URL.revokeObjectURL(oldestBlobUrl);
  }
};

const fetchEntityPreviewBlobUrl = async (
  contentHash: string,
  imageUrl: string,
): Promise<string | null> => {
  const cachedBlobUrl = touchEntityPreviewBlobCacheEntry(contentHash);
  if (cachedBlobUrl) {
    return cachedBlobUrl;
  }

  const pendingLookup = pendingEntityPreviewBlobUrlLookups.get(contentHash);
  if (pendingLookup) {
    return pendingLookup;
  }

  const lookupPromise = fetch(imageUrl, { cache: "force-cache" })
    .then(async response => {
      if (!response.ok) {
        return null;
      }

      const imageBlob = await response.blob();
      const blobUrl = URL.createObjectURL(imageBlob);
      setEntityPreviewBlobCacheEntry(contentHash, blobUrl);

      return blobUrl;
    })
    .catch(() => null)
    .finally(() => {
      pendingEntityPreviewBlobUrlLookups.delete(contentHash);
    });

  pendingEntityPreviewBlobUrlLookups.set(contentHash, lookupPromise);
  return lookupPromise;
};

export const useEntityPreviewBlobUrl = ({
  imageUrl,
  contentHash,
}: {
  imageUrl: string | undefined;
  contentHash: string | undefined;
}) => {
  const [resolvedImageUrl, setResolvedImageUrl] = useState<string | null>(imageUrl ?? null);
  const [isResolvingBlobUrl, setIsResolvingBlobUrl] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    if (!imageUrl) {
      setResolvedImageUrl(null);
      setIsResolvingBlobUrl(false);
      return () => {
        isCancelled = true;
      };
    }

    if (!contentHash) {
      setResolvedImageUrl(imageUrl);
      setIsResolvingBlobUrl(false);
      return () => {
        isCancelled = true;
      };
    }

    const cachedBlobUrl = touchEntityPreviewBlobCacheEntry(contentHash);
    if (cachedBlobUrl) {
      setResolvedImageUrl(cachedBlobUrl);
      setIsResolvingBlobUrl(false);
      return () => {
        isCancelled = true;
      };
    }

    setResolvedImageUrl(null);
    setIsResolvingBlobUrl(true);

    void fetchEntityPreviewBlobUrl(contentHash, imageUrl)
      .then(blobUrl => {
        if (isCancelled) {
          return;
        }

        setResolvedImageUrl(blobUrl ?? imageUrl);
      })
      .finally(() => {
        if (!isCancelled) {
          setIsResolvingBlobUrl(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [contentHash, imageUrl]);

  return { resolvedImageUrl, isResolvingBlobUrl };
};
